import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { OwnerService, OwnerStyle } from '../../../@backend';
import { ConfigService } from '../../../@vex/config/config.service';
import { AuthenticationService } from './authentication.service';

const DEFAULT_OWNER_LOGO_URL = 'assets/img/icons/favicons/favicon.svg';

@Injectable({ providedIn: 'root' })
export class OwnerThemeService {
  constructor(
    private readonly ownerService: OwnerService,
    private readonly configService: ConfigService,
    private readonly authService: AuthenticationService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  async applyCurrentOwnerStyle(): Promise<void> {
    if(!this.authService.isLoggedIn) return;

    try {
      const style = await firstValueFrom(
        this.ownerService.ownerControllerGetOwnerStyle(
          this.authService.currentOwnerId,
        ),
      );
      this.apply(style);
    } catch(error) {
      console.error('Owner style could not be loaded.', error);
    }
  }

  apply(style: Pick<
    OwnerStyle,
    | 'mainColor'
    | 'secondaryColor'
    | 'sidebarBackgroundColor'
    | 'sidebarItemHoverColor'
    | 'sidebarItemActiveColor'
    | 'sidebarTextColor'
    | 'sidebarActiveTextColor'
    | 'toolbarBackgroundColor'
    | 'contentBackgroundColor'
    | 'logo'
  >): void {
    this.configService.updateConfig({
      style: {
        colors: {
          primary: {
            default: this.hexToRgb(style.mainColor),
            contrast: this.hexToRgb(style.secondaryColor),
          },
        },
      },
    });

    this.document.body.style.setProperty(
      '--sidenav-background',
      style.sidebarBackgroundColor,
    );
    this.document.body.style.setProperty(
      '--sidenav-toolbar-background',
      style.sidebarBackgroundColor,
    );
    this.document.body.style.setProperty(
      '--sidenav-item-background-hover',
      style.sidebarItemHoverColor,
    );
    this.document.body.style.setProperty(
      '--sidenav-item-dropdown-background-hover',
      style.sidebarItemHoverColor,
    );
    this.document.body.style.setProperty(
      '--sidenav-item-background-active',
      style.sidebarItemActiveColor,
    );
    this.document.body.style.setProperty(
      '--sidenav-item-dropdown-background-active',
      style.sidebarItemActiveColor,
    );
    this.document.body.style.setProperty(
      '--sidenav-item-color',
      style.sidebarTextColor,
    );
    this.document.body.style.setProperty(
      '--sidenav-item-color-active',
      style.sidebarActiveTextColor,
    );
    this.document.body.style.setProperty(
      '--sidenav-item-dropdown-color-active',
      style.sidebarActiveTextColor,
    );
    this.document.body.style.setProperty(
      '--sidenav-item-icon-color-active',
      style.sidebarActiveTextColor,
    );
    this.document.body.style.setProperty(
      '--toolbar-background',
      style.toolbarBackgroundColor,
    );
    this.document.body.style.setProperty(
      '--secondary-toolbar-background',
      style.toolbarBackgroundColor,
    );
    this.document.body.style.setProperty(
      '--background-base',
      style.contentBackgroundColor,
    );

    const logoUrl = style.logo || DEFAULT_OWNER_LOGO_URL;
    this.configService.updateConfig({
      sidenav: {
        imageUrl: logoUrl,
        imageDarkUrl: logoUrl,
      },
    });
  }

  private hexToRgb(color: string): string {
    const normalized = color.replace('#', '');
    const red = parseInt(normalized.substring(0, 2), 16);
    const green = parseInt(normalized.substring(2, 4), 16);
    const blue = parseInt(normalized.substring(4, 6), 16);
    return `rgb(${red}, ${green}, ${blue})`;
  }
}
